/**
 * Filtering for the published mark scheme picker.
 *
 * The whole catalogue is fetched once and narrowed in the browser, so the
 * picker can grey out boards that have nothing rather than only revealing that
 * after the teacher selects one. Kept as pure functions with no DOM access so
 * the rules can be tested directly.
 *
 * A scheme can serve several subjects (AQA's science ladders cover Combined
 * Science, Biology, Chemistry and Physics), so subject matching always reads
 * the `subjects` array rather than the singular `subject`.
 */
(function (root) {
    'use strict';

    // Fixed order, always shown. A board with nothing for the current
    // selection is disabled rather than dropped, so positions never move.
    const BOARDS = ['AQA', 'Edexcel', 'OCR'];
    const LEVELS = ['GCSE', 'A-Level'];
    const ANY = '';

    const subjectsOf = (t) => (t && t.subjects && t.subjects.length ? t.subjects : [t.subject]);

    /** Schemes matching a selection. An empty string on any axis means "any". */
    function filterSchemes(catalogue, selection) {
        const { subject = ANY, board = ANY, level = ANY } = selection || {};
        return (catalogue || []).filter(t =>
            (!board || t.board === board)
            && (!level || t.level === level)
            && (!subject || subjectsOf(t).includes(subject)));
    }

    /**
     * Subjects worth offering: every subject with at least one scheme at the
     * selected level. The teacher's own subject is pinned first and always
     * present – if it has no schemes yet, seeing it selected and empty explains
     * more than silently omitting it.
     */
    function subjectOptions(catalogue, { level = ANY, ownSubject = null } = {}) {
        const atLevel = filterSchemes(catalogue, { level });
        const found = new Set();
        for (const t of atLevel) for (const s of subjectsOf(t)) found.add(s);
        const rest = [...found].filter(s => s !== ownSubject).sort((a, b) => a.localeCompare(b));
        return ownSubject ? [ownSubject, ...rest] : rest;
    }

    /** How many schemes each board has for this subject and level. */
    function boardCounts(catalogue, { subject = ANY, level = ANY } = {}) {
        const counts = {};
        for (const b of BOARDS) counts[b] = filterSchemes(catalogue, { subject, level, board: b }).length;
        return counts;
    }

    /**
     * Resolve a selection against the catalogue: which boards are selectable,
     * and whether the requested board survives. A board that has gone empty
     * falls back to "all boards" so the teacher sees what does exist instead of
     * an empty list.
     */
    function resolveSelection(catalogue, selection) {
        const { subject = ANY, level = ANY, board = ANY } = selection || {};
        const counts = boardCounts(catalogue, { subject, level });
        const usable = !board || counts[board] > 0;
        const resolvedBoard = usable ? board : ANY;
        return {
            subject,
            level,
            board: resolvedBoard,
            boardFellBack: !usable,
            counts,
            schemes: filterSchemes(catalogue, { subject, level, board: resolvedBoard }),
        };
    }

    /**
     * What to print as a scheme's subject in the picker.
     *
     * A shared scheme has a headline `subject` that is simply the first of the
     * subjects it serves, so printing it raw labels the AQA writing grid
     * "French" even when the teacher has filtered to Spanish. Prefer the
     * subject they actually asked for; with no filter, say the scheme is shared
     * rather than pretending it belongs to one subject.
     */
    function displaySubject(scheme, selectedSubject) {
        const subs = subjectsOf(scheme);
        if (selectedSubject && subs.includes(selectedSubject)) return selectedSubject;
        if (subs.length === 1) return subs[0];
        return `${subs[0]} +${subs.length - 1} more`;
    }

    /** Every subject a scheme serves – for the row's tooltip. */
    const allSubjects = (scheme) => subjectsOf(scheme).join(', ');

    /** Wording for the empty state, naming whichever axes are pinned. */
    function emptyMessage({ subject = ANY, board = ANY, level = ANY } = {}) {
        const bits = [board, level, subject].filter(Boolean);
        return bits.length
            ? `No published scheme yet for ${bits.join(' ')}. Draft one with AI instead.`
            : 'No published mark schemes available. Draft one with AI instead.';
    }

    const api = {
        BOARDS, LEVELS, filterSchemes, subjectOptions, boardCounts,
        resolveSelection, emptyMessage, displaySubject, allSubjects,
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    else root.MarkSchemeFilter = api;
}(typeof globalThis !== 'undefined' ? globalThis : this));
